import type { CSSProperties, ReactNode } from "react";
import type { LomdaBackgrounds, LomdaTheme } from "@yedalabs/schema";

import { hexToRGB, isHexColor } from "@/lib/colors/helpers/converters";
import type { PageBackground } from "@/lib/utils/lomdaV3/types";

import "./lomdaBackground.css";

/** Shape of the retired POC field — may still exist in stored lomda metadata. */
interface LegacyBackgroundImage {
    desktopUrl?: string;
    mobileUrl?: string;
    surface?: boolean;
}

/**
 * Resolve a theme's background set, converting the retired POC field
 * (`theme.backgroundImage` — desktop/mobile URLs) on the fly so lomdot saved
 * before the ratio-based system keep their backdrop. Editor saves don't strip
 * unknown metadata fields, so the legacy value survives in storage and this
 * read-time adapter is enough — no data migration needed.
 */
export function resolveLomdaBackgrounds(
    theme: LomdaTheme | undefined
): LomdaBackgrounds | undefined {
    if (theme?.backgrounds) return theme.backgrounds;
    const legacy = (
        theme as (LomdaTheme & { backgroundImage?: LegacyBackgroundImage }) | undefined
    )?.backgroundImage;
    if (!legacy?.desktopUrl) return undefined;
    return {
        images: [
            { ratioW: 16, ratioH: 9, url: legacy.desktopUrl },
            ...(legacy.mobileUrl
                ? [{ ratioW: 9, ratioH: 16, url: legacy.mobileUrl }]
                : []),
        ],
        surface: legacy.surface,
    };
}

/** Public URLs can contain quotes/parens that break `url(...)` — escape. */
const cssUrl = (u: string) => `url("${u.replace(/"/g, "%22")}")`;

/**
 * Turns the brand-kit background set into a fixed full-viewport backdrop for
 * the V3 preview/published view. Returns `null` when the set has no images, so
 * the caller keeps its flat `bg-background`.
 *
 * Selection is pure CSS — no JS resize listeners, no flash: images are sorted
 * by aspect ratio and the boundaries between neighbours are their GEOMETRIC
 * means, emitted as `@media (min-aspect-ratio: …)` rules that swap the
 * `--lomda-bg-url` variable. The screen always gets the image whose ratio is
 * closest to its own; the base (no query) rule is the narrowest image.
 *
 * The optional scrim renders as a translucent dark gradient layered OVER the
 * image (still under the content), for text readability on busy artwork.
 */
export function lomdaBackdrop(
    backgrounds: LomdaBackgrounds | null | undefined
): { className: string; style: CSSProperties; css: string } | null {
    const images = backgrounds?.images ?? [];
    if (images.length === 0) return null;

    const sorted = [...images].sort(
        (a, b) => a.ratioW / a.ratioH - b.ratioW / b.ratioH
    );

    let css = `.lomda-bg { --lomda-bg-url: ${cssUrl(sorted[0].url)}; }\n`;
    for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const cur = sorted[i];
        // Geometric mean of the two neighbour ratios = the screen ratio at
        // which the closer image flips from one to the other.
        const threshold = Math.sqrt(
            (prev.ratioW / prev.ratioH) * (cur.ratioW / cur.ratioH)
        );
        const num = Math.round(threshold * 100000);
        css += `@media (min-aspect-ratio: ${num}/100000) { .lomda-bg { --lomda-bg-url: ${cssUrl(cur.url)}; } }\n`;
    }

    const scrim = backgrounds?.scrim ?? 0;
    const style: CSSProperties = {};
    if (scrim > 0) {
        (style as Record<string, string>)["--lomda-bg-scrim"] =
            `linear-gradient(rgba(0,0,0,${scrim}), rgba(0,0,0,${scrim}))`;
    }

    return { className: "lomda-bg", style, css };
}

/** A first-pass theme key or any non-hex colour draws nothing; an opacity
 *  outside 0–1 draws at 1. */
export function resolvePageBackground(
    background: PageBackground | undefined
): Required<PageBackground> | undefined {
    if (!background || !isHexColor(background.color)) return undefined;
    const { opacity } = background;
    return {
        color: background.color,
        opacity:
            typeof opacity === "number" && opacity >= 0 && opacity <= 1
                ? opacity
                : 1,
    };
}

function pageBackgroundLayer(
    background: PageBackground | undefined
): string | undefined {
    const resolved = resolvePageBackground(background);
    if (!resolved) return undefined;
    const { r, g, b } = hexToRGB(resolved.color);
    const fill = `rgb(${r} ${g} ${b} / ${resolved.opacity})`;
    return `linear-gradient(${fill}, ${fill})`;
}

/**
 * A page's own colour as `--lomda-page-bg`: a layer of `.lomda-bg` (above the
 * scrim) on a backdrop, else drawn over the flat Background.
 */
export function pageBackgroundStyle(
    background: PageBackground | undefined,
    onBackdrop: boolean
): CSSProperties | undefined {
    const layer = pageBackgroundLayer(background);
    if (!layer) return undefined;
    return {
        "--lomda-page-bg": layer,
        ...(!onBackdrop && { backgroundImage: "var(--lomda-page-bg)" }),
    } as CSSProperties;
}

/**
 * The frosted panel the lomda content sits on when a background image is set,
 * so text stays readable over the image (the "content on a surface").
 */
export function LomdaSurface({ children }: { children: ReactNode }) {
    return (
        <div
            className="rounded-2xl p-6 shadow-xl ring-1 ring-black/5 backdrop-blur-md md:p-10"
            style={{ backgroundColor: "hsl(var(--lomda-background) / 0.85)" }}
        >
            {children}
        </div>
    );
}
